<template>
  <KeysLayout v-slot="s">
    <template v-for="(k, id) in s.keys" :key="id">
      <KeyboardButton
        ref="buttons"
        :row="k.row"
        :col="k.col"
        :x="k.key.x"
        :y="k.key.y"
        :rot="k.key.rot"
        :height="k.key.size.y"
        :width="k.key.size.x"
        @dragstart="dragStart"
        @dragenter="dragEnter"
        @dragleave="dragLeave"
        @dragend="dragEnd"
        @drop="drop"
      />
    </template>

    <div
      class="absolute bottom-3 left-3 flex justify-center items-center gap-2 hide-on-action"
      :class="actionHighlight ? 'hide' : ''"
    >
      <QuickToolButton tooltip="Search for keys with specific actions">
        <template #icon>
          <Icon icon="material-symbols:search-rounded" class="text-lg m-auto" />
        </template>

        <template #popup="slotProps">
          <!-- Avoid calculating action search tags when not needed, but preserve state after closing popup -->
          <KeepAlive>
            <ActionSearch
              v-if="slotProps.visible"
              @searchChanged="() => slotProps.forceUpdate()"
              @highlightChanged="(on) => (actionHighlight = on)"
            />
          </KeepAlive>
        </template>
      </QuickToolButton>

      <QuickToolButton tooltip="Quickly apply keys preset">
        <template #icon>
          <Icon
            icon="material-symbols:menu-book-outline"
            class="text-lg m-auto"
          />
        </template>

        <template #popup>
          <PresetSelect />
        </template>
      </QuickToolButton>
    </div>

    <div
      class="absolute bottom-3 right-3 tooltip"
      :class="sizeInfo.exceeded ? 'tooltip-error' : ''"
      data-tip="Estimated configuration memory usage (may be inaccurate). If too much space is used then firmware compilation will fail."
      @click="sizeHover = !sizeHover"
    >
      <span
        class="transition-all btn btn-ghost btn-sm normal-case"
        :class="sizeInfo.exceeded ? 'animate-pulse font-bold text-error' : ''"
      >
        <template v-if="sizeHover">
          {{ sizeInfo.used.value.toFixed(1) }}
          {{ sizeInfo.used.prefix + "B" }} /
          {{ sizeInfo.available.value.toFixed(1) }}
          {{ sizeInfo.available.prefix + "B" }}
        </template>
        <template v-else> {{ sizeInfo.percent.toFixed(0) }}% </template>
      </span>
    </div>
  </KeysLayout>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { humanSize, ESTIMATED_AVAILABLE_SIZE } from "@/keyboard";
import { useKeyboardStore } from "@/stores";
import KeysLayout from "@/components/utils/KeysLayout.vue";
import KeyboardButton from "@/components/keys/KeyboardButton.vue";
import KeyCap from "@/components/keys/KeyCap.vue";
import ActionSearch from "@/components/keys/ActionSearch.vue";
import QuickToolButton from "@/components/keys/QuickToolButton.vue";
import PresetSelect from "@/components/keys/PresetSelect.vue";

const buttons = ref<InstanceType<typeof KeyboardButton>[]>([]);

const kb = useKeyboardStore();

const sizeHover = ref(false);
const sizeInfo = computed(() => {
  const used = humanSize(kb.getEstimatedConfigSize());
  const available = humanSize(ESTIMATED_AVAILABLE_SIZE);
  const percent = (used.bytes / available.bytes) * 100;
  return {
    used,
    available,
    percent,
    exceeded: used.bytes >= available.bytes,
  };
});

const actionHighlight = ref(false);

// Drag & drop
type KeyCapT = InstanceType<typeof KeyCap>;
const dragged = ref<KeyCapT | null>(null);
const draggedOver = ref<KeyCapT | null>(null);

const dragStart = (e: DragEvent, kc: KeyCapT) => {
  if (e.dataTransfer == null) return;
  dragged.value = kc;
  e.dataTransfer.dropEffect = "move";
  e.dataTransfer.setData("text/plain", "");
};

const dragEnter = (e: DragEvent, kc: KeyCapT) => {
  if (e.dataTransfer == null || dragged.value == null) return;
  e.dataTransfer.dropEffect = "move";

  const isDragStart =
    kc.$props.row == dragged.value.$props.row &&
    kc.$props.col == dragged.value.$props.col;
  if (!isDragStart) {
    // Clear 'dragover' on all buttons to prevent leaving some buttons
    // with 'dragover' when dragenter/dragleave happen in unexpected order.
    buttons.value.forEach((b) => {
      b.$refs.keycap.$el.classList.remove("dragover");
    });
    draggedOver.value = kc;
    kc.$el.classList.add("dragover");
  }
};

const dragLeave = (e: DragEvent, kc: KeyCapT) => {
  const isSame =
    draggedOver.value != null &&
    kc.$props.row == draggedOver.value.$props.row &&
    kc.$props.col == draggedOver.value.$props.col;

  if (draggedOver.value == null || !isSame) {
    kc.$el.classList.remove("dragover");
  }

  draggedOver.value = null;
};

const dragEnd = (_e: DragEvent, _kc: KeyCapT) => {
  buttons.value.forEach((b) => {
    b.$refs.keycap.$el.classList.remove("dragover");
  });
};

// const swapDurationMs = 300;
const drop = (e: DragEvent, kc: KeyCapT) => {
  if (dragged.value == null) return;

  // Animate swapping by temporarily adding translate() styles that move
  // to the destination along with "swapping" class for smooth transition.
  // TODO: handle rotation difference
  const box = (elem: HTMLElement) => elem.getBoundingClientRect();
  const from = dragged.value.$el;
  const to = kc.$el;
  const dx = box(to).left - box(from).left;
  const dy = box(to).top - box(from).top;
  const drot = kc.$props.rot - dragged.value.$props.rot;

  from.classList.add("dnd-swapping");
  to.classList.add("dnd-swapping");
  from.style.transform = `translate(${dx}px, ${dy}px) rotate(${-drot}deg)`;
  to.style.transform = `translate(${-dx}px, ${-dy}px) rotate(${drot}deg)`;

  setTimeout(() => {
    // HACK: Stop transition animation to avoid keys "going back" to their positions
    // This is needed to override defaults animation settings for KeyCap
    from.classList.add("dnd-no-transition");
    to.classList.add("dnd-no-transition");

    // Move keys back to where they were
    from.style.transform = "";
    to.style.transform = "";

    // Revert back to normal transition settings as soon as possible
    setTimeout(() => {
      from.classList.remove("dnd-swapping");
      to.classList.remove("dnd-swapping");
      from.classList.remove("dnd-no-transition");
      to.classList.remove("dnd-no-transition");
    }, 300);

    // Finally perform the state change
    if (dragged.value == null) return;
    const dragRow = dragged.value.$props.row;
    const dragCol = dragged.value.$props.col;
    const [dropRow, dropCol] = [kc.$props.row, kc.$props.col];

    const dragAct = kb.getAction(dragRow, dragCol);
    const dropAct = kb.getAction(dropRow, dropCol);
    kb.setAction(dragRow, dragCol, dropAct);
    kb.setAction(dropRow, dropCol, dragAct);
  }, 300 /* swapDurationMs */);
};
</script>

<style>
.keycap.dnd-swapping {
  /* transition: transform v-bind(swapDurationMs + "ms") ease-in-out; */
  @apply transition-transform duration-300;
}
.keycap.dnd-swapping.dnd-no-transition {
  @apply transition-none;
}
</style>

<style scoped>
.hide-on-action {
  @apply transition-opacity ease-in-out duration-500;
}

.hide-on-action.hide {
  @apply opacity-10;
}
</style>
